/**
 * Wishlist page — reads product IDs from WishlistContext (localStorage) and
 * hydrates via GET /products/:id. Reuses ProductCard so everything (badges,
 * qty stepper, wishlist heart) is consistent with the rest of the storefront.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useWishlist } from "@/contexts/WishlistContext";
import ProductCard from "@/components/customer/ProductCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Heart, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

export default function Wishlist() {
  const { user } = useAuth();
  const { ids, clear } = useWishlist();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user || ids.length === 0) { setItems([]); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const rows = await Promise.all(ids.map((id) => api.get(`/products/${id}`).then((r) => r.data).catch(() => null)));
        if (!cancelled) setItems(rows.filter(Boolean));
      } catch (e) { if (!cancelled) toast.error(errMsg(e)); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [ids, user]);

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-16 text-center" data-testid="wishlist-signin">
        <Heart className="w-8 h-8 mx-auto text-primary mb-2" />
        <h1 className="font-display text-3xl font-semibold">Sign in to view your wishlist</h1>
        <p className="mt-2 text-muted-foreground">Your saved items sync with your Babu Baazar account.</p>
        <Button asChild size="lg" className="mt-6 btn-primary-glow"><Link to="/login?redirect=/wishlist">Sign in</Link></Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6" data-testid="wishlist-page">
      <div className="flex items-baseline justify-between mb-4">
        <h1 className="font-display text-2xl md:text-3xl font-semibold flex items-center gap-2">
          <Heart className="w-5 h-5 text-primary" /> Your wishlist
        </h1>
        {ids.length > 0 && (
          <Button variant="ghost" size="sm" onClick={clear} data-testid="wishlist-clear">Clear all</Button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="aspect-square rounded-xl" />)}
        </div>
      ) : ids.length === 0 || items.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-10 text-center bg-card" data-testid="wishlist-empty">
          <Heart className="w-10 h-10 mx-auto text-primary/60 mb-3" />
          <div className="font-display text-2xl font-semibold">Your wishlist is empty</div>
          <p className="text-sm text-muted-foreground mt-1 mb-4">Tap the heart on any product to save it here.</p>
          <Button asChild className="btn-primary-glow"><Link to="/shop"><ShoppingBag className="w-4 h-4 mr-1.5" />Start shopping</Link></Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4" data-testid="wishlist-grid">
          {items.map((p) => (
            <div key={p.id} className="fade-in-up"><ProductCard p={p} /></div>
          ))}
        </div>
      )}
    </div>
  );
}
